import { Controller } from "@hotwired/stimulus"
import { debounce } from "javascripts/helpers"

export default class extends Controller {
  static targets = ["input", "suggestions"] // No change here, Stimulus maps data-*-target to this
  static values = {
    searchUrl: {type: String, default: '/hr/employees/search'},
    minLength: { type: Number, default: 1 }
  }

  connect() {
    this.currentQuery = ""
    this.selectedIndex = -1
    this.suggestions = []
    this.performSearch = debounce(this.fetchSuggestions.bind(this), 300)

    document.addEventListener("click", this.handleClickOutside.bind(this))
    this.inputTarget.addEventListener("keydown", this.onKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside.bind(this))
    this.inputTarget.removeEventListener("keydown", this.onKeydown.bind(this))

    // this.performSearch.cancel()
  }

  search() {
    const text = this.inputTarget.value
    const cursorPosition = this.inputTarget.selectionStart

    // Updated regex to include Arabic characters and other Unicode "word characters"
    // \p{L} matches any kind of letter from any language (Unicode property)
    // \p{N} matches any kind of numeric character in any script
    // \p{Mn} matches a combining mark
    // Use the 'u' flag for Unicode support.
    const lastAtMatch = text.substring(0, cursorPosition).match(/@([\p{L}\p{N}\p{Mn}_]*)$/u);

    if (!lastAtMatch) {
      this.hideSuggestions();
      return
    }

    this.currentQuery = lastAtMatch[1]
    this.mentionStartIndex = cursorPosition - this.currentQuery.length - 1
    this.mentionStartPosition = cursorPosition - this.currentQuery.length - 1

    if (this.currentQuery.length >= this.minLengthValue) {
      this.performSearch(this.currentQuery)
    } else if (this.currentQuery.length === 0 ) {
      console.log('No query')
      // this.hideSuggestions()
      this.showRecentEmployees()
    } else {
      this.hideSuggestions()
    }
  }

  fetchSuggestions(query) {
    fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`)
      .then(response => response.json())
      .then(data => {
        this.suggestions = data
        this.renderSuggestions(data)
      })
      .catch(error => {
        console.error("Error fetching suggestions:", error)
        this.hideSuggestions()
      })
  }

  async showRecentEmployees() {
    try {
      const response = await fetch(`${this.searchUrlValue}?recent=true`)
      const employees = await response.json()

      this.suggestions = employees
      this.renderSuggestions(employees)
    } catch (error) {
      console.error('Error fetching recent employees:', error)
    }
  }

  renderSuggestions(employees) {
    this.suggestionsTarget.innerHTML = ""

    if (employees.length === 0) {
      this.hideSuggestions()
      return
    }

    employees.forEach((employee, index) => {
      const item = document.createElement("div")
      item.classList.add("suggestion-item", "p-2", "cursor-pointer", "hover:bg-gray-200")
      if (index === this.selectedIndex) {
        item.classList.add("bg-gray-200") // Highlight selected item
      }
      item.textContent = employee.name
      item.dataset.employeeId = employee.id
      item.dataset.employeeName = employee.name
      item.dataset.action = "click->mention#selectSuggestion"
      this.suggestionsTarget.appendChild(item)
    })
    this.suggestionsTarget.classList.remove("hidden")
    this.selectedIndex = -1 // Reset selection on new suggestions
    this.positionSuggestions()
  }

  positionSuggestions() {
    const input = this.inputTarget
    const inputRect = input.getBoundingClientRect()
    const suggestions = this.suggestionsTarget

    // Position below the input
    suggestions.style.position = 'absolute'
    suggestions.style.top = `${inputRect.height + window.scrollY}px`
    // suggestions.style.left = `${inputRect.left + window.scrollX}px`
    // suggestions.style.width = `${inputRect.width}px`
    suggestions.style.zIndex = '1000'
  }

  selectSuggestion(event) {
    const employeeId = event.currentTarget.dataset.employeeId
    const employeeName = event.currentTarget.dataset.employeeName

    this.insertMention(employeeId, employeeName)
  }

  insertMention(id, name) {
    const input = this.inputTarget
    const beforeMention = input.value.substring(0, this.mentionStartPosition)
    const afterMention = input.value.substring(input.selectionStart)

    const mentionText = `@[${name}](${id}) `
    const newValue = beforeMention + mentionText + afterMention
    const newCursorPosition = beforeMention.length + mentionText.length

    input.value = newValue
    input.setSelectionRange(newCursorPosition, newCursorPosition)
    input.focus()

    this.hideSuggestions()

    // Trigger input event to update any bindings
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }

  hideSuggestions() {
    this.suggestionsTarget.classList.add("hidden")
    this.suggestionsTarget.innerHTML = ""
    this.selectedIndex = -1
  }

  // Keyboard navigation for suggestions
  onKeydown(event) {
    if (this.suggestionsTarget.classList.contains("hidden")) return

    if (this.suggestions.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveSelection('down')
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveSelection('up')
    } else if (event.key === "Enter" || event.key === "Tab") {
      if (this.selectedIndex !== -1) {
        event.preventDefault() // Prevent form submission or tabbing out
        const selected = this.suggestions[this.selectedIndex]
        this.insertMention(selected.id, selected.name)
      }
    } else if (event.key === "Escape") {
      this.hideSuggestions()
    }
  }

  moveSelection(direction) {
    if (direction === "down") {
      this.selectedIndex = (this.selectedIndex + 1) % this.suggestions.length
    } else if (direction === "up") {
      this.selectedIndex = (this.selectedIndex - 1 + this.suggestions.length) % this.suggestions.length
    }
    this.highlightSelectedItem()
  }

  highlightSelectedItem() {
    const items = Array.from(this.suggestionsTarget.children)

    items.forEach((item, index) => {
      item.classList.toggle("bg-gray-200", index === this.selectedIndex)
    })
    // Optional: Scroll into view if suggestions are long
    if (this.selectedIndex !== -1) {
      items[this.selectedIndex].scrollIntoView({ block: "nearest" })
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }
}
